import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { useDashboardQuery } from '../../Components/Dashboard/api/dashboardEndPoint';
import { useAuthContext } from '../../Context/AuthContext/AuthContext';
import { imgUrl, url } from '../../Helpers/utils/Constant';
import { getToken } from '../../rest/utils/get-token';
import { showToast } from '../../Toaster/Toaster';
import CommonOfflinePaymentInfo from '../new-member-applications/IndexFile/paymentInformation/offline/CommonOfflinePaymentInfo';
import { usePendingYearDataQuery } from './api/renewalEndpoints';
import ImageUploaderWithPreview from '../../Common/ImageUploaderWithPreview';
import { useRenewalQuery } from '../../rest/useFetchRenewalApplication';
type IProps = {
  handelClose: Dispatch<SetStateAction<boolean>>;
  setReload: Function;
};

const CreateRenewalApplication = ({ handelClose, setReload }: IProps) => {
  const { user } = useAuthContext();
  const { dashboardData } = useDashboardQuery(user?.user_member_id);
  const { data } = usePendingYearDataQuery();
  const { data: renewalDraftList, fetchData: fetchRenewalDraft } =
    useRenewalQuery();
  const draftData = renewalDraftList?.[0];

  useEffect(() => {
    if (user?.user_member_id) {
      fetchRenewalDraft({
        renewal_application_member_id: user.user_member_id,
        renewal_application_status: 'draft',
      });
    }
  }, [user?.user_member_id, fetchRenewalDraft]);

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors },
    watch,
    setValue,
    control,
    getValues,
  } = useForm<any>({
    defaultValues: {
      bank_ac_no: '00890320000141',
      amount: '12300',
      bank_name: 'Trust Bank (Kakrail Branch)',
    },
  });
  const amount = watch('amount');
  const idCard = watch('idCard');

  useEffect(() => {
    if (draftData) {
      if (draftData.civil_aviation_number) {
        setValue('civil_aviation_number', draftData.civil_aviation_number);
      }
      if (draftData.civil_aviation_issue_date) {
        setValue(
          'civil_aviation_issue_date',
          moment(draftData.civil_aviation_issue_date).format('YYYY-MM-DD'),
        );
      }
      if (draftData.civil_aviation_expiry_date) {
        setValue(
          'civil_aviation_expiry_date',
          moment(draftData.civil_aviation_expiry_date).format('YYYY-MM-DD'),
        );
      }
      if (draftData.trade_license_number) {
        setValue('trade_license_number', draftData.trade_license_number);
      }
      if (draftData.trade_license_issue_date) {
        setValue(
          'trade_license_issue_date',
          moment(draftData.trade_license_issue_date).format('YYYY-MM-DD'),
        );
      }
      if (draftData.trade_license_expiry_date) {
        setValue(
          'trade_license_expiry_date',
          moment(draftData.trade_license_expiry_date).format('YYYY-MM-DD'),
        );
      }
      if (draftData.tin_number) {
        setValue('tin_number', draftData.tin_number);
      }
      if (draftData.nid_no) {
        setValue('nid_no', draftData.nid_no);
      }
    }
  }, [draftData, setValue]);

  useEffect(() => {
    if (idCard && data?.data?.totalAmount) {
      setValue('amount', data?.data.totalAmount + 300);
    } else {
      setValue('amount', data?.data.totalAmount);
    }
  }, [idCard, setValue, data?.data]);

  const [loading, setLoading] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const token = getToken();

  const onSubmit = async (value: any, asDraft: boolean = false) => {
    const formData = new FormData();

    const paymentFields = [
      'type',
      'bank_ac_no',
      'deposit_date',
      'bank_name',
      'branch_name',
      'amount',
      'bank_slip',
      'reference_no',
    ];

    if (asDraft) {
      formData.append('status', 'draft');
    } else {
      if (value.type === 'online') {
        formData.append(
          'success_page',
          `${window.location.origin}/payment-success`,
        );
        formData.append(
          'failed_page',
          `${window.location.origin}/payment-failed`,
        );
        formData.append(
          'cancelled_page',
          `${window.location.origin}/payment-reject`,
        );
      }
    }

    Object.keys(value).forEach((item) => {
      if (asDraft && paymentFields.includes(item)) {
        return;
      }

      if (typeof value[item] === 'object' && value[item] !== null) {
        if (value[item].length > 0) {
          formData.append(item, value[item][0]);
        }
      } else if (
        value[item] !== undefined &&
        value[item] !== null &&
        value[item] !== ''
      ) {
        formData.append(item, value[item]);
      }
    });

    formData.append(
      'renewal_application_member_id',
      user?.user_member_id?.toString(),
    );

    try {
      setLoading(true);
      setIsDraft(asDraft);
      const response = await fetch(`${url}/api/member/renewal-application`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || ''}`,
        },
        body: formData,
      });

      const responseData = await response.json();

      if (responseData.success) {
        if (!asDraft && responseData?.data?.redirect_url) {
          window.open(
            responseData?.data?.redirect_url,
            '_blank',
            'noopener,noreferrer',
          );
        }
        showToast(
          responseData.message ||
          (asDraft
            ? 'Application saved as draft successfully'
            : 'Application submitted successfully'),
          'success',
        );
        handelClose(false);
        setReload();
      } else {
        setLoading(false);
        setIsDraft(false);
        showToast(
          responseData.message ||
          (asDraft
            ? 'Failed to save application as draft'
            : 'Failed to submit application'),
          'error',
        );
      }
    } catch (error: any) {
      showToast(error?.message || 'An error occurred', 'error');
      setLoading(false);
      setIsDraft(false);
    }
  };

  const handleSaveDraft = () => {
    const values = getValues();
    onSubmit(values, true);
  };

  return (
    <>
      {data?.data?.paid ? (
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              p: 3,
              backgroundColor: '#e6f4ea',
              borderRadius: 2,
              border: '1px solid #b6dfc4',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />

            <Box>
              <Typography
                variant='h6'
                sx={{ fontWeight: 'bold', color: 'success.main', mb: 1 }}
              >
                You're all set!
              </Typography>

              <Typography
                variant='body1'
                sx={{ fontSize: '1rem', color: 'text.primary' }}
              >
                You’ve successfully renewed your membership up to the year&nbsp;
                <strong>{data?.data?.lastRenewalYear || ''}</strong>.
                <br />
                No further action is needed at this time.
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          component='form'
          onSubmit={handleSubmit((data) => onSubmit(data, false))}
          p={2}
          pt={0}
        >
          <Grid container spacing={2} mb={2} maxWidth={'md'}>
            <Grid xs={12} md={4} lg={4} item>
              <ImageUploaderWithPreview
                register={register}
                setError={setError}
                clearErrors={clearErrors}
                errors={errors}
                label='Updated civil aviation file ( max size 5mb )'
                fileName='renewal_application_updated_civil_aviation_file'
                required
                imgSerUrl={
                  draftData?.renewal_application_updated_civil_aviation_file ||
                  null
                }
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <ImageUploaderWithPreview
                register={register}
                setError={setError}
                clearErrors={clearErrors}
                errors={errors}
                label='Updated trade license file ( max size 5mb )'
                fileName='renewal_application_updated_trade_license_file'
                required
                imgSerUrl={
                  draftData?.renewal_application_updated_trade_license_file ||
                  null
                }
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <ImageUploaderWithPreview
                register={register}
                setError={setError}
                clearErrors={clearErrors}
                errors={errors}
                label='Proof of Submission of Tax Return (Acknowledgement Receipts) (max size 5mb)'
                fileName='renewal_application_tax_return_certificate_file'
                required
                imgSerUrl={
                  draftData?.renewal_application_tax_return_certificate_file ||
                  null
                }
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <ImageUploaderWithPreview
                register={register}
                setError={setError}
                clearErrors={clearErrors}
                errors={errors}
                label='Previous Certificate file from ATAB ( max size 5mb )'
                fileName='renewal_application_prev_atab_certificate_file'
                required
                imgSerUrl={
                  draftData?.renewal_application_prev_atab_certificate_file ||
                  null
                }
              />
            </Grid>

            <Grid xs={12} md={4} lg={4} item>
              <ImageUploaderWithPreview
                register={register}
                setError={setError}
                clearErrors={clearErrors}
                errors={errors}
                label='Office Deed (Max size 5mb)'
                fileName='office_deed_file'
                required
                imgSerUrl={draftData?.office_deed_file || null}
              />
            </Grid>
            {dashboardData?.company_ownership_status === 'Incorporated' && (
              <>
                <Grid xs={12} md={4} lg={4} item>
                  <ImageUploaderWithPreview
                    register={register}
                    setError={setError}
                    clearErrors={clearErrors}
                    errors={errors}
                    label='Ltd. Co. all Docs (latest)'
                    fileName='ltd_docs'
                    required
                    imgSerUrl={draftData?.ltd_docs || null}
                  />
                </Grid>
                <Grid xs={12} md={4} lg={4} item>
                  <ImageUploaderWithPreview
                    register={register}
                    setError={setError}
                    clearErrors={clearErrors}
                    errors={errors}
                    label='Schedule 10/12'
                    fileName='schedule_10_12'
                    required
                    imgSerUrl={draftData?.schedule_10_12 || null}
                  />
                </Grid>
              </>
            )}
            <Grid xs={12} md={4} lg={4} item>
              <TextField
                fullWidth
                label='Civil Aviation Certificate Number'
                required
                id='outlined-required'
                placeholder='Civil aviation number'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('civil_aviation_number')}
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <TextField
                fullWidth
                label='Civil Aviation Certificate Issue Date'
                required
                id='outlined-required'
                placeholder='Civil Aviation Certificate Issue Date'
                type='date'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('civil_aviation_issue_date')}
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <TextField
                fullWidth
                label='Civil Aviation Certificate Expiry Date'
                required
                id='outlined-required'
                placeholder='Civil Aviation Certificate Expiry Date'
                type='date'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('civil_aviation_expiry_date')}
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <TextField
                fullWidth
                label='Trade License Certificate Number'
                required
                id='outlined-required'
                placeholder='Trade License Certificate Number'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('trade_license_number')}
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <TextField
                fullWidth
                label='Trade License Certificate Issue date'
                required
                id='outlined-required'
                placeholder='Trade License Certificate Issue date'
                type='date'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('trade_license_issue_date')}
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <TextField
                fullWidth
                label='Trade License Certificate Expiry Date'
                required
                id='outlined-required'
                placeholder='Trade License Certificate Expiry Date'
                type='date'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('trade_license_expiry_date')}
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <TextField
                fullWidth
                label='Tin Number'
                required
                id='outlined-required'
                placeholder='Tin Number'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('tin_number')}
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <TextField
                fullWidth
                label='NID Number'
                required
                id='outlined-required'
                placeholder='NID Number'
                InputLabelProps={{
                  shrink: true,
                }}
                {...register('nid_no', {
                  required: 'NID Number is required',
                  pattern: {
                    value: /^[0-9]+$/,
                    message: 'Only numbers are allowed',
                  },
                  minLength: {
                    value: 10,
                    message: 'NID number must be at least 10 digits',
                  },
                })}
                error={errors['nid_no']?.message ? true : false}
                helperText={
                  errors['nid_no']?.message
                    ? String(errors['nid_no']?.message)
                    : ''
                }
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <ImageUploaderWithPreview
                register={register}
                setError={setError}
                clearErrors={clearErrors}
                errors={errors}
                label='Upload NID Both Side (Max size 5mb)'
                fileName='nid_file'
                required
                imgSerUrl={draftData?.nid_file || null}
              />
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <a
                href='https://m360ict.s3.ap-south-1.amazonaws.com/uploads/UploadedFiles/underTakingFile/undertaking_form(2).doc'
                download='undertaking_form.doc'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textDecoration: 'none',
                  backgroundColor: '#f2f5ef',
                  color: '#573d3d',
                  padding: '14px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d3cece',
                  fontSize: '15px',
                  fontWeight: 'initial',
                }}
              >
                <DownloadIcon style={{ marginRight: '10px', color: 'green' }} />
                Download Undertaking Form
              </a>
            </Grid>
            <Grid xs={12} md={4} lg={4} item>
              <ImageUploaderWithPreview
                register={register}
                setError={setError}
                clearErrors={clearErrors}
                errors={errors}
                label='Upload Undertaking File (Max size 5mb)'
                fileName='under_taking_file'
                required
                imgSerUrl={draftData?.under_taking_file || null}
              />
            </Grid>
            <Grid xs={12} item>
              <Alert severity='error' icon={<DownloadIcon />}>
                Note: Download the undertaking form and print in office pad with
                seal & Signature then Upload.
              </Alert>
            </Grid>

            {/* ID Card Checkbox */}
            <Grid item xs={12}>
              <Grid
                container
                alignItems='center'
                justifyContent='space-between'
                spacing={2}
              >
                {/* ID Card Fee */}
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...register('idCard')}
                        value={true}
                        defaultChecked={true}
                        color='primary'
                      />
                    }
                    label='ID Card Fee for (03 Years) TK 300.00'
                  />
                </Grid>

                <Grid item>
                  <Card
                    elevation={3}
                    sx={{
                      maxWidth: 500,
                      mx: 'auto',
                      borderRadius: 2,
                      background: '#f0f4f9',
                      textAlign: 'center',
                    }}
                  >
                    {!data?.data?.paid && (
                      <Box sx={{ padding: 1 }}>
                        <Typography variant='h6' gutterBottom color='primary'>
                          Renewal Years ({data?.data?.totalAmount + ' TK.' || 0}
                          )
                        </Typography>

                        <Typography
                          variant='body2'
                          color='text.secondary'
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          mb={2}
                        >
                          <CalendarMonthIcon sx={{ mr: 1 }} />
                          You are renewing for the following years:
                        </Typography>
                        <Stack
                          direction='row'
                          spacing={1}
                          flexWrap='wrap'
                          useFlexGap
                          sx={{
                            maxWidth: '100%',
                            justifyContent: {
                              xs: 'center', // mobile
                              sm: 'flex-start', // small screens
                            },
                            rowGap: 2, // adds vertical spacing between rows
                          }}
                        >
                          {data?.data?.yearsRange?.split('-').map((year) => (
                            <Chip
                              size='small'
                              key={year}
                              label={year}
                              color='info'
                              variant='filled'
                              sx={{
                                fontSize: '.8rem',
                                minWidth: 70,
                                px: 2,
                                borderRadius: 2,
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            <Grid xs={12} item sx={{ my: 2 }}>
              <Divider>
                <Chip label='Payment Information' size='small' />
              </Divider>
            </Grid>
            <Grid xs={12} item>
              <CommonOfflinePaymentInfo
                clearErrors={clearErrors}
                setError={setError}
                errors={errors}
                register={register}
                setValue={setValue}
                control={control}
                onlinePaymentAmount={amount}
              />
            </Grid>
          </Grid>

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={2}
            justifyContent='flex-end'
            alignItems='center'
            mt={4}
          >
            <Button
              type='button'
              variant='outlined'
              color='primary'
              disabled={loading}
              onClick={handleSaveDraft}
              startIcon={
                loading && isDraft ? (
                  <CircularProgress size={18} color='inherit' />
                ) : null
              }
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: 160,
                py: 1,
                fontWeight: 600,
                fontSize: '0.95rem',
              }}
            >
              <span>{loading && isDraft ? 'Saving Draft...' : 'Save As Draft'}</span>
            </Button>

            <Button
              type='submit'
              variant='contained'
              color='primary'
              disabled={loading}
              disableElevation
              startIcon={
                loading && !isDraft ? (
                  <CircularProgress size={18} color='inherit' />
                ) : null
              }
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: 160,
                py: 1,
                fontWeight: 600,
                fontSize: '0.95rem',
              }}
            >
              <span>{loading && !isDraft ? 'Submitting...' : 'Submit'}</span>
            </Button>
          </Stack>
        </Box>
      )}
    </>
  );
};

export default CreateRenewalApplication;
